import api from './api';
import { ChunkedUploadInit, VideoStatus, FrameInfo } from '../types';

export interface ThumbnailUploadResult {
  _id: string;
  url: string;
  filename: string;
}

const IMAGE_UPLOAD_TIMEOUT_MS = 60000;

// Retry once on network/timeout errors so a single hung request on flaky links
// (e.g. mainland China → S3) does not strand the upload promise forever.
function isRetriableUploadError(err: any): boolean {
  if (err?.response) return false; // server responded — don't retry
  const code = err?.code;
  return code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT';
}

async function postImageWithRetry<T>(
  url: string,
  formData: FormData,
): Promise<T> {
  let lastError: any;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: IMAGE_UPLOAD_TIMEOUT_MS,
      });
      return response.data;
    } catch (err: any) {
      lastError = err;
      if (!isRetriableUploadError(err)) throw err;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 2000));
    }
  }
  throw lastError;
}

export const mediaService = {
  // ==================
  // Thumbnail Operations
  // ==================

  async uploadThumbnail(
    file: File,
    targetType?: string,
    targetId?: string
  ): Promise<ThumbnailUploadResult> {
    const formData = new FormData();
    formData.append('thumbnail', file);
    if (targetType) formData.append('targetType', targetType);
    if (targetId) formData.append('targetId', targetId);

    return postImageWithRetry<ThumbnailUploadResult>('/media/thumbnails', formData);
  },

  async deleteThumbnail(id: string): Promise<void> {
    await api.delete(`/media/thumbnails/${id}`);
  },

  // ==================
  // Avatar Operations
  // ==================

  async uploadAvatar(file: File): Promise<ThumbnailUploadResult> {
    const formData = new FormData();
    formData.append('avatar', file);

    return postImageWithRetry<ThumbnailUploadResult>('/media/avatars', formData);
  },

  async deleteAvatar(id: string): Promise<void> {
    await api.delete(`/media/avatars/${id}`);
  },

  // ==================
  // Video Operations
  // ==================

  async initVideoUpload(
    filename: string,
    fileSize: number,
    targetType: string,
    targetId: string
  ): Promise<ChunkedUploadInit> {
    const response = await api.post('/media/videos/init', {
      filename,
      fileSize,
      targetType,
      targetId,
    });
    return response.data;
  },

  async uploadChunk(
    uploadId: string,
    chunk: Blob,
    chunkIndex: number,
    signal?: AbortSignal
  ): Promise<{ received: number; progress: number; totalReceived: number; totalChunks: number }> {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunkIndex', chunkIndex.toString());

    const response = await api.post(`/media/videos/chunk/${uploadId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      signal,
    });
    return response.data;
  },

  async completeVideoUpload(uploadId: string, signal?: AbortSignal): Promise<{ mediaId: string; status: string; message: string }> {
    const response = await api.post(`/media/videos/complete/${uploadId}`, undefined, {
      timeout: 30000,
      signal,
    });
    return response.data;
  },

  async getVideoStatus(mediaId: string): Promise<VideoStatus> {
    const response = await api.get(`/media/videos/${mediaId}/status`);
    return response.data;
  },

  async deleteVideo(id: string): Promise<void> {
    await api.delete(`/media/videos/${id}`);
  },

  async getVideoFrames(mediaId: string): Promise<{ frames: FrameInfo[]; coverIndex: number | null }> {
    const response = await api.get(`/media/videos/${mediaId}/frames`);
    return response.data;
  },

  async setVideoCover(mediaId: string, frameIndex: number): Promise<{ coverUrl: string }> {
    const response = await api.post(`/media/videos/${mediaId}/set-cover`, { frameIndex });
    return response.data;
  },

  getVideoStreamUrl(mediaId: string): string {
    return `/api/media/videos/${mediaId}/stream`;
  },

  async getSignedVideoUrl(mediaId: string): Promise<{
    url: string;
    fallbackUrl?: string;
    compatUrl?: string | null;
    compatCnUrl?: string | null;
    cnUrl?: string | null;
    region?: string;
    duration: number;
    expiresIn?: number;
    watermark?: { name: string; email: string; uid: string };
  }> {
    const response = await api.post(`/media/videos/token/${mediaId}`);
    return {
      url: response.data.url,
      fallbackUrl: response.data.fallbackUrl,
      compatUrl: response.data.compatUrl,
      compatCnUrl: response.data.compatCnUrl,
      cnUrl: response.data.cnUrl,
      region: response.data.region,
      duration: response.data.duration || 0,
      expiresIn: response.data.expiresIn,
      watermark: response.data.watermark,
    };
  },

  async getPublicVideoUrl(mediaId: string): Promise<{
    url: string;
    fallbackUrl?: string;
    compatUrl?: string | null;
    compatCnUrl?: string | null;
    cnUrl?: string | null;
    region?: string;
    duration: number;
    expiresIn?: number;
  }> {
    const response = await api.get(`/media/videos/public-token/${mediaId}`);
    return {
      url: response.data.url,
      fallbackUrl: response.data.fallbackUrl,
      compatUrl: response.data.compatUrl,
      compatCnUrl: response.data.compatCnUrl,
      cnUrl: response.data.cnUrl,
      region: response.data.region,
      duration: response.data.duration || 0,
      expiresIn: response.data.expiresIn,
    };
  },

  // ==================
  // Chunked Upload Helper
  // ==================

  async uploadVideo(
    file: File,
    targetType: string,
    targetId: string,
    onProgress?: (progress: number, stage: 'uploading' | 'processing') => void,
    signal?: AbortSignal
  ): Promise<{ mediaId: string; status: 'ready' | 'processing' }> {
    // Initialize upload
    const initResult = await this.initVideoUpload(
      file.name,
      file.size,
      targetType,
      targetId
    );

    const { uploadId, chunkSize, totalChunks } = initResult;

    // Upload chunks with retry logic
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const maxRetries = 2;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (signal?.aborted) {
            throw new DOMException('Upload cancelled', 'AbortError');
          }

          const result = await this.uploadChunk(uploadId, chunk, i, signal);

          if (onProgress) {
            const uploadProgress = (result.progress / 100) * 90;
            onProgress(uploadProgress, 'uploading');
          }
          lastError = null;
          break;
        } catch (err: any) {
          if (err.name === 'AbortError' || err.name === 'CanceledError') {
            throw err;
          }
          lastError = err;
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (lastError) {
        throw new Error(`Failed to upload chunk ${i + 1}/${totalChunks} after ${maxRetries + 1} attempts: ${lastError.message}`);
      }
    }

    // Complete upload
    const completeResult = await this.completeVideoUpload(uploadId, signal);

    if (completeResult.status === 'ready') {
      // Duplicate detected - video already processed
      if (onProgress) {
        onProgress(100, 'processing');
      }
      return { mediaId: completeResult.mediaId, status: 'ready' as const };
    }

    if (onProgress) {
      onProgress(90, 'processing');
    }

    return { mediaId: completeResult.mediaId, status: 'processing' as const };
  },

  // Poll for video status until ready or failed
  async waitForVideoReady(
    mediaId: string,
    onProgress?: (progress: number) => void,
    pollInterval = 2000,
    maxWaitTime = 600000 // 10 minutes
  ): Promise<VideoStatus> {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      const status = await this.getVideoStatus(mediaId);

      if (onProgress) {
        onProgress(90 + (status.progress / 100) * 10);
      }

      if (status.status === 'ready' || status.status === 'failed') {
        return status;
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Video processing timeout');
  },
};
